import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { getUser, getUserSummary } from 'store/fetchers/users'
import { getStorageItem, setStorageItem, STORAGE_KEYS } from 'store/reducers/storage'
import type { IFormattedUser, IUserSummary } from 'store/fetchers/users/types'
import type { AppState, AppThunk } from 'store'
import { setApiUserToken } from 'config/api'
import { isJwtExpired } from '../../../utils/is-jwt-expired'

export const storeKey = '@redux/user'

export enum LoginStatuses {
  LOGIN = 'LOGIN',
  INIT = 'INIT',
  LOGOUT = 'LOGOUT',
  ERR = 'ERR',
}

export interface IUser {
  id: string
  email: string
  phone: string
  isActive2Fa: boolean
  referral_code?: string
  toaster_text?: string
  toaster_category?: string
  toaster_title?: string
  fallback_email?: string
  loans_disabled?: boolean
  kyc_verified?: string
  twitter_username?: string
  is_show_vip_banner?: boolean
  is_dynamic_loans_available?: boolean
  is_liquidation_portfolio_enabled?: boolean
}

export interface IRateState {
  user: IUser
  authToken: string
  authStatus: LoginStatuses
  errAuthCode?: string
  userSummary: IUserSummary
}

const token = getStorageItem(STORAGE_KEYS.AUTH_TOKEN)
const isTokenValid = token && !isJwtExpired(token)

const initialState: IRateState = {
  user: null,
  authToken: isTokenValid ? LoginStatuses.LOGIN : LoginStatuses.LOGOUT,
  authStatus: null,
  errAuthCode: null,
  userSummary: null,
}

export const ratesSlice = createSlice({
  name: storeKey,
  initialState,
  reducers: {
    setUserData: (state: IRateState, action: PayloadAction<IFormattedUser>) => {
      if (action.payload) {
        state.user = {
          phone: action.payload.phone,
          email: action.payload.email,
          id: action.payload.id,
          isActive2Fa: action.payload.is_active_2fa,
          toaster_text: action.payload.toaster_text,
          toaster_category: action.payload.toaster_category,
          toaster_title: action.payload.toaster_title,
          fallback_email: action.payload.fallback_email,
          loans_disabled: action.payload.loans_disabled,
          kyc_verified: action.payload.kyc_verified,
          twitter_username: action.payload.twitter_username,
          is_show_vip_banner: action.payload.is_show_vip_banner,
          is_dynamic_loans_available: action.payload?.is_dynamic_loans_available,
          is_liquidation_portfolio_enabled: action.payload?.is_liquidation_portfolio_enabled,
        }
      } else {
        state.user = initialState.user
      }
    },
    setAuthStatus: (state: IRateState, action: PayloadAction<LoginStatuses>) => {
      state.authStatus = action.payload
    },
    setUserToken: (state: IRateState, action: PayloadAction<string>) => {
      state.authToken = action.payload
      setStorageItem(STORAGE_KEYS.AUTH_TOKEN, action.payload)
    },
    setErrAuthCode: (state: IRateState, action: PayloadAction<string>) => {
      state.errAuthCode = action.payload
    },
    setUserSummary: (state: IRateState, action: PayloadAction<IUserSummary>) => {
      state.userSummary = action.payload
    },
    updateToasterData: (state: IRateState, action: PayloadAction<Pick<IUser, 'toaster_text' | 'toaster_category' | 'toaster_title'>>) => {
      state.user = {
        ...state.user,
        toaster_text: action.payload.toaster_text,
        toaster_category: action.payload.toaster_category,
        toaster_title: action.payload.toaster_title,
      }
    }
  },
})

export const { setUserData, setUserToken, setAuthStatus, setErrAuthCode, setUserSummary, updateToasterData } = ratesSlice.actions

export const selectedUser = (state: AppState) => state[storeKey].user || initialState.user
export const selectedUserAuthToken = (state: AppState) => state[storeKey].authToken || initialState.authToken
export const selectedAuthStatus = (state: AppState): LoginStatuses => state[storeKey].authStatus
export const selectedErrAuthCode = (state: AppState): string => state[storeKey].errAuthCode
export const selectedUserSummary = (state: AppState) => state[storeKey].userSummary

export const fetchUser = (): AppThunk => async (dispatch) => {
  try {
    const user = await getUser()
    if (user) {
      await dispatch(setAuthStatus(LoginStatuses.LOGIN))
    }
    await dispatch(setUserData(user))
  } catch (err) {
    if (err.message.includes('Failed to fetch')) {
      await dispatch(setAuthStatus(LoginStatuses.ERR))
      await dispatch(setErrAuthCode('0'))
    } else if (err.message.includes('500')) {
      await dispatch(setAuthStatus(LoginStatuses.ERR))
      await dispatch(setErrAuthCode('500'))
    } else if (err.message.includes('403')) {
      await dispatch(setAuthStatus(LoginStatuses.LOGOUT))
    }
  }
}

export const fetchUserSummary = (): AppThunk => async (dispatch) => {
  try {
    const data = await getUserSummary()
    await dispatch(setUserSummary(data))
  } catch (err) {
    return null
  }
}

export const checkUserLogin = (): AppThunk => async (dispatch, getState) => {
  const token = getStorageItem(STORAGE_KEYS.AUTH_TOKEN)
  if (!token || isJwtExpired(token)) {
    await dispatch(setUserData(null))
    await dispatch(setAuthStatus(LoginStatuses.LOGOUT))
    return
  }

  if (token) {
    await dispatch(setAuthStatus(LoginStatuses.LOGIN))
    await dispatch(setUserAuthToken(token))
    const user = selectedUser(getState())

    if (!user) {
      await dispatch(setUserData(null))
    }
  }
}

export const setUserAuthToken = (token: string): AppThunk<Promise<void>> => async (dispatch) => {
  await dispatch(setUserToken(token))
  await setApiUserToken(token)
  if (token) {
    // await dispatch(setAuthStatus(LoginStatuses.LOGIN))
    await dispatch(fetchUser())
  }
}

export const doLogout = (): AppThunk<Promise<void>> => async (dispatch) => {
  await dispatch(setAuthStatus(LoginStatuses.LOGOUT))
  await dispatch(setUserToken(''))
  await dispatch(setUserData(null))
  localStorage.setItem('store:AUTH_TOKEN', '')
  setApiUserToken('')
}

export default ratesSlice.reducer
